import { ref, readonly } from "vue";
import {
  appointmentApi,
  type AppointmentView,
  type BookAppointmentInput,
} from "@/api/appointment.api";

/**
 * Reactive wrapper for customer appointment lists.
 * Phase 2: keeps `upcoming` + `past` lists in memory and exposes
 * book/cancel/refresh helpers. No polling — caller refreshes after writes.
 */
export function useCustomerAppointments() {
  const upcoming = ref<AppointmentView[]>([]);
  const past = ref<AppointmentView[]>([]);
  const pastTotal = ref(0);
  const pastHasMore = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);

  async function refreshUpcoming(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      upcoming.value = await appointmentApi.listUpcoming();
    } catch (e) {
      error.value = (e as Error).message;
    } finally {
      loading.value = false;
    }
  }

  async function refreshPast(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const res = await appointmentApi.listPast();
      past.value = res.items;
      pastTotal.value = res.total;
      pastHasMore.value = res.hasMore;
    } catch (e) {
      error.value = (e as Error).message;
    } finally {
      loading.value = false;
    }
  }

  // Append the next page onto `past` without resetting loading/error so the
  // UI can keep the existing rows visible while spinning a "โหลดเพิ่ม" button.
  async function loadMorePast(limit = 20): Promise<void> {
    if (!pastHasMore.value) return;
    try {
      const res = await appointmentApi.listPast({
        offset: past.value.length,
        limit,
      });
      past.value = [...past.value, ...res.items];
      pastTotal.value = res.total;
      pastHasMore.value = res.hasMore;
    } catch (e) {
      error.value = (e as Error).message;
    }
  }

  async function book(input: BookAppointmentInput): Promise<AppointmentView> {
    const created = await appointmentApi.book(input);
    await refreshUpcoming();
    return created;
  }

  async function cancel(id: string, reason?: string): Promise<void> {
    await appointmentApi.cancel(id, reason);
    await Promise.all([refreshUpcoming(), refreshPast()]);
  }

  // Phase 6 — reschedule own appointment.
  async function reschedule(
    id: string,
    input: {
      scheduledAt: string;
      doctorHospId?: string;
      doctorUsrid?: string;
      reason?: string;
    },
  ): Promise<AppointmentView> {
    const updated = await appointmentApi.reschedule(id, input);
    await refreshUpcoming();
    return updated;
  }

  return {
    upcoming: readonly(upcoming),
    past: readonly(past),
    pastTotal: readonly(pastTotal),
    pastHasMore: readonly(pastHasMore),
    loading: readonly(loading),
    error: readonly(error),
    refreshUpcoming,
    refreshPast,
    loadMorePast,
    book,
    cancel,
    reschedule,
  };
}
